import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Star, MapPin, Tag } from "lucide-react";
import { loadDiscoverStores } from "../../store/storeSlices/storeDiscoverSlice";
import { useNavigate } from "react-router-dom";
import { getListDiscountSummary } from "../../utils/rewardUtils";
import StoreTags from "./StoreTags";

const formatDistance = (km) =>
    km < 1 ? `${Math.round(km * 1000)} m away` : `${km.toFixed(1)} km away`;

const formatDiscount = (discount) => {
    const summary = getListDiscountSummary(discount);
    if (!summary) return null;

    const minOrder = summary.minOrderValue > 0 ? ` on ₹${summary.minOrderValue}+` : "";

    // show only what exists: discount, cashback, or both
    const text = [summary.discountText, summary.cashbackText].filter(Boolean).join(" + ");

    return text ? `${text}${minOrder}` : null;
};

export default function StoreDiscoverList({ city, lat, lon }) {
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const { items, nextCursor, loading, loadingMore, error } = useSelector((s) => s.storeDiscover);
    const { selected } = useSelector((s) => s.storeCategories);
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (city && selected) {
            dispatch(loadDiscoverStores({ city, category: selected, lat, lon, reset: true }));
        }
        // lat/lon intentionally excluded — they only add a display-only distanceKm,
        // re-fetching on every minor coordinate change would be wasteful.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [city, selected, dispatch]);

    const loadMore = useCallback(() => {
        if (!loadingMore && nextCursor) {
            dispatch(loadDiscoverStores({ city, category: selected, lat, lon, reset: false }));
        }
    }, [dispatch, city, selected, lat, lon, loadingMore, nextCursor]);

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) loadMore();
        }, { rootMargin: "200px" });
        observer.observe(el);
        return () => observer.disconnect();
    }, [loadMore]);

    if (!selected) return null;
    if (loading) {
        return (
            <div className="w-full max-w-2xl mt-4 flex items-center gap-2 text-sm text-gray-400">
                <Loader2 size={14} className="animate-spin" /> Loading {selected} stores...
            </div>
        );
    }
    if (error) return <p className="w-full max-w-2xl mt-4 text-sm text-red-600">{error}</p>;
    if (!items.length) {
        return <p className="w-full max-w-2xl mt-4 text-sm text-gray-500">No {selected} stores in {city} yet.</p>;
    }

    return (
        <div className="w-full max-w-2xl mt-4 space-y-3">
            {items.map((store) => (
                <div onClick={() => navigate(`/web/store-details?storeId=${store.id}`)} key={store.id} className="flex cursor-pointer gap-3 p-3 bg-white border border-gray-200 rounded-xl">
                    <img
                        src={store.logo || "/store-placeholder.png"}
                        alt={store.name}
                        className="w-16 h-16 rounded-lg object-cover shrink-0 bg-gray-100"
                    />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-800 truncate">{store.name}</p>
                        <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                            <MapPin size={12} /> {store.address}
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {store.rating != null && (
                                <span className="flex items-center gap-1">
                                    <Star size={12} className="text-[#0B67FC] fill-[#0B67FC]" />
                                    {store.rating} ({store.reviewCount})
                                </span>
                            )}
                            {formatDiscount(store.discount) && (
                                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                    <Tag size={12} />
                                    {formatDiscount(store.discount)}
                                </span>
                            )}
                        </div>
                        {store.distanceKm != null && (
                            <p className="text-xs font-medium text-[#0B67FC] mt-1">
                                {formatDistance(store.distanceKm)}
                            </p>
                        )}
                        <StoreTags store={store} className="mt-1.5" />
                    </div>
                </div>
            ))}
            <div ref={sentinelRef} className="h-6 flex items-center justify-center">
                {loadingMore && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>
        </div>
    );
}